import React, { type ReactNode } from 'react';
import { Card as AntdCard, type CardProps as AntdCardProps } from 'antd';
import { css, useTheme } from '@emotion/react';
import styled from 'types/theme.types';
import { typeStyle } from 'constants/typography';
import CommonCard from 'components/common/card';

/**
 * antd's surface plus the props the wrapper lifted off it. Derivations pass `bodyPadding` where they used
 * to write an `.ant-card-body` rule, so the bridge has to accept it — it is dropped again on the legacy
 * path, where that rule still supplies the padding.
 */
export type BridgedCardProps = AntdCardProps & { bodyPadding?: string };

/**
 * The seam between antd's Card prop surface and the wrapper's — the one call site to adapt if they
 * diverge.
 *
 * `bordered={false}` has no direct equivalent: the wrapper's `inline` drops the box border but adds a
 * bottom rule unless `inlineBottomBorder` suppresses it, so borderless needs both. Only
 * `bodyStyle.padding` is carried across, because that is the only declaration any call site passes —
 * cloud-resource-modal-summary's `{ padding: '12px' }`. Widen this if another site passes more, rather
 * than dropping declarations silently.
 */
export const toCommonProps = (props: BridgedCardProps) => {
  const { bodyStyle, bordered, hoverable, cover, title, children, ...rest } = props;

  const fromBodyStyle = typeof bodyStyle?.padding === 'string' ? bodyStyle.padding : undefined;

  return {
    ...rest,
    title,
    children,
    hoverable,
    cover,
    // Spread conditionally rather than assigned: a plain `bodyPadding: fromBodyStyle` overwrites the
    // `bodyPadding` a derivation passes through `...rest` with `undefined` whenever there is no
    // `bodyStyle`, silently dropping it.
    ...(fromBodyStyle ? { bodyPadding: fromBodyStyle } : {}),
    ...(bordered === false ? { inline: true, inlineBottomBorder: true } : {})
  };
};

/**
 * For the 14 call sites importing Card straight from antd. Legacy renders antd's Card verbatim, so
 * switch-off parity is true by construction; the new design routes through the wrapper so there is one
 * card implementation on that path. APO-484 removes the branch and leaves every site on the wrapper —
 * this is the grep for that sweep.
 *
 * Shape note: this follows `BridgedAlert` / `BridgedTooltip`, which switch implementation across the
 * branch. It is NOT `BridgedDropdown`'s shape — that one renders antd on both sides and only adds a
 * new-design patch, because antd's Dropdown is what the design wants in both states.
 */
export const BridgedCard: React.FC<BridgedCardProps> = props => {
  const theme = useTheme();

  if (theme.isNewDesign) return <CommonCard {...toCommonProps(props)} />;

  // `bodyPadding` is the wrapper's prop, not antd's: forwarded to AntdCard it would land on the DOM and
  // draw a React unknown-prop warning. Switch-off the derivation's own .ant-card-body rule owns padding.
  const { bodyPadding: _bodyPadding, ...antdProps } = props;

  return <AntdCard {...antdProps} />;
};

type CardMetaProps = {
  avatar?: ReactNode;
  title?: ReactNode;
  description?: ReactNode;
  className?: string;
};

/**
 * antd scopes every `.ant-card-meta*` rule as `.ant-card .ant-card-meta*` - card/style/index.js nests the
 * meta block inside `[componentCls]` - and `Meta` itself has no `useStyle`, only `getPrefixCls` and
 * classNames. So on the new-design path, where BridgedCard renders the wrapper's plain div and no
 * `.ant-card` ever mounts, antd injects nothing and the avatar/title/description lose their layout
 * entirely. Being inside the card body is not enough; the CSS needs the ancestor.
 *
 * Hence a real seam rather than a re-export. Legacy keeps antd's Meta, which still sits under a real
 * antd Card and so keeps antd's CSS; the new design owns the same layout from tokens. Class names mirror
 * antd's shape without borrowing its prefix, matching how the wrapper names `custom-card-body`.
 *
 * Values track antd's genCardMetaStyle so the two paths read alike: flex row, avatar gutter, detail
 * column takes the remainder, title at the 16px heading style, description in secondary.
 */
const MetaRoot = styled.div`
  display: flex;
`;

const MetaAvatar = styled.div`
  ${({ theme }) => css`
    padding-inline-end: ${theme.spacing.lg};
  `}
`;

const MetaDetail = styled.div`
  overflow: hidden;
  flex: 1;

  ${({ theme }) => css`
    > *:not(:last-child) {
      margin-bottom: ${theme.spacing.xs};
    }
  `}
`;

const MetaTitle = styled.div`
  ${typeStyle.headingH4}
  color: var(--text-heading);
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
`;

const MetaDescription = styled.div`
  ${typeStyle.bodyMd}
  color: var(--text-secondary);
`;

export const CardMeta: React.FC<CardMetaProps> = ({ avatar, title, description, className }) => {
  const theme = useTheme();

  if (!theme.isNewDesign) {
    return <AntdCard.Meta avatar={avatar} title={title} description={description} className={className} />;
  }

  return (
    <MetaRoot className={`card-meta${className ? ` ${className}` : ''}`}>
      {avatar && <MetaAvatar className="card-meta-avatar">{avatar}</MetaAvatar>}
      {(title || description) && (
        <MetaDetail className="card-meta-detail">
          {title && <MetaTitle className="card-meta-title">{title}</MetaTitle>}
          {description && <MetaDescription className="card-meta-description">{description}</MetaDescription>}
        </MetaDetail>
      )}
    </MetaRoot>
  );
};

export default BridgedCard;
